import { useEffect } from 'react';
import { Select, Switch, Text } from '@rsos/base-components';
import { InfoTip } from '@rsos/components/capstone/base/Tooltip/InfoTip/InfoTip';
import styled from '@rsos/theme/styled';
import ConditionalFeatureDisplay from '../../../Common/ConditionalFeatureDisplay/ConditionalFeatureDisplay';
import { useChatContext } from '../ChatContext';
import {
  LanguageOption,
  useTranslationLanguageOptions,
} from './useTranslationLanguageOptions';

const TranslationMenuContainer = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 16px;
  align-self: stretch;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.lightLineDivider};
  background: ${({ theme }) => theme.colors.foreground};
  color: ${({ theme }) => theme.colors.secondaryText};
  > label {
    font-weight: normal;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  align-self: stretch;
  /* Switch has an empty label with a top and bottom margin 
     for reasons I cannot fathom and do not desire here. */
  & label {
    margin: 0;
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

export const TranslationMenu = () => {
  const {
    refreshConversationData,
    isTranslationMenuOpen,
    isTranslatingOutgoingMessages,
    translateOutgoingMessages,
    translationLanguage,
    setTranslationLanguage,
    detectedLanguage,
  } = useChatContext();

  // The detected language may change mid-conversation. Fetch and display the
  // most recent detected language when the menu was opened.
  useEffect(() => {
    if (isTranslationMenuOpen) {
      refreshConversationData();
    }
  }, [isTranslationMenuOpen, refreshConversationData]);

  const options = useTranslationLanguageOptions(detectedLanguage);

  const selectedOption = translationLanguage
    ? options.find(o => o.value === translationLanguage)
    : options.find(o => o.value === detectedLanguage);

  if (!isTranslationMenuOpen) {
    return;
  }

  return (
    <ConditionalFeatureDisplay
      dataName="chat-translation-menu"
      conditions={[{ featureFlagEnabled: 'text-manual-translation-override' }]}
    >
      <TranslationMenuContainer>
        <label>
          <Text size="small">Citizen language:</Text>
        </label>
        <Select
          options={options}
          value={selectedOption}
          onChange={(v: LanguageOption) => setTranslationLanguage(v?.data)}
          isClearable
        />
        <ToggleContainer>
          <InfoTip>
            Turn on to translate your messages into the citizen's language.
          </InfoTip>
          <Text size="small">Auto-translate your messages:</Text>
          <Spacer />
          <Switch
            isOn={isTranslatingOutgoingMessages}
            onClick={() =>
              translateOutgoingMessages(!isTranslatingOutgoingMessages)
            }
            onOffLabel
          />
        </ToggleContainer>
      </TranslationMenuContainer>
    </ConditionalFeatureDisplay>
  );
};
